/**
 * CORE-2098 — вход истёк: одно место, где это замечают, и один сигнал наверх.
 *
 * CORE-2092 обновляет токен за полчаса до истечения. Но если вкладка проспала
 * дольше (ноутбук закрыт на ночь), токен к первому запросу уже мёртв, а
 * обновить мёртвый сервер не даёт (`POST /api/Auth/refresh` под `[Authorize]`).
 * Запросы получают 401, и человек видел общие ошибки вместо «войдите снова».
 *
 * Правило: «вход истёк» — это 401 на запрос С ТОКЕНОМ, когда срок токена по
 * часам уже прошёл или сервер уже отказался обновить именно этот токен (401 на
 * refresh). Одиночный 401 при живом токене сюда не относится: он не доказывает,
 * что сессии больше нет. Гость без токена — тоже: ему 401 законно отвечают
 * закрытые ручки. Сам отказ refresh окна не поднимает (его зовёт и таймер, когда
 * человек ничего не делает) — окно поднимает следующий упавший запрос.
 *
 * Сигнал один на пачку: пять упавших загрузок подряд — одно окно. После «Позже»
 * окно вернётся не раньше чем через минуту и только на новый отказ.
 *
 * Только веб: на нативе ответ на 401 остаётся прежним (`handleResponse`).
 */
import { Platform } from 'react-native';
import { getJwtExpiryMs } from './jwtExpiry';

/** Окно предложения показывается только на вебе; натив живёт по-старому. */
export const SESSION_EXPIRY_PROMPT_ENABLED = Platform.OS === 'web';

/** Сколько молчать после «Позже». */
export const SESSION_EXPIRY_SNOOZE_MS = 60 * 1000;

/** Срок токена по часам прошёл. Без `exp` — не знаем, значит не истёк. */
export function isJwtExpired(token: string, nowMs: number): boolean {
  const expMs = getJwtExpiryMs(token);
  return expMs != null && expMs <= nowMs;
}

type Listener = () => void;
const listeners = new Set<Listener>();
let pending = false;
let snoozedAt = -Infinity;
/** Токен, обновить который сервер отказался (401 на refresh). */
let rejectedToken: string | null = null;

/** Подписка на «вход истёк». Возвращает отписку. */
export function subscribeSessionExpired(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** Сервер подтвердил, что сессии нет. Пачка вызовов даёт один сигнал. */
export function reportSessionExpired(nowMs = Date.now()): void {
  if (pending) return;
  if (nowMs - snoozedAt < SESSION_EXPIRY_SNOOZE_MS) return;
  pending = true;
  listeners.forEach((l) => {
    try { l(); } catch { /* слушатель не должен ронять запрос */ }
  });
}

/** Уже замечено и ещё не разрешено входом. */
export function isSessionExpiredPending(): boolean {
  return pending;
}

/** Сервер ответил 401 на обновление этого токена: он больше не годится. */
export function markRefreshRejected(token: string): void {
  rejectedToken = token;
}

/**
 * 401 на запрос с токеном `token`: вход истёк? Если да — сигнал (один на
 * пачку) и `true`, и вызывающий не делает ничего резкого сам.
 */
export function noteUnauthorized(token: string | null, nowMs = Date.now()): boolean {
  if (!token) return false;
  if (token !== rejectedToken && !isJwtExpired(token, nowMs)) return false;
  reportSessionExpired(nowMs);
  return true;
}

/** «Позже»: окно закрыто, следующий отказ покажет его не раньше чем через минуту. */
export function snoozeSessionExpired(nowMs = Date.now()): void {
  pending = false;
  snoozedAt = nowMs;
}

/** Человек вошёл (или вышел): всё сначала. */
export function resetSessionExpired(): void {
  pending = false;
  snoozedAt = -Infinity;
  rejectedToken = null;
}
